import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Lightbulb, Code2, ArrowRight, Users, Zap, Target } from 'lucide-react';

const Services = () => {
  const { t } = useTranslation();

  const services = [
    {
      icon: Lightbulb,
      title: t('services.dtTitle'),
      description: t('services.dtDesc'),
      capabilities: [
        { icon: Target, text: t('services.dtCap1') },
        { icon: Zap, text: t('services.dtCap2') },
        { icon: Users, text: t('services.dtCap3') },
      ],
      benefits: [
        t('services.dtDeliver1'),
        t('services.dtDeliver2'),
        t('services.dtDeliver3'),
        t('services.dtDeliver4'),
        t('services.dtDeliver5'),
      ],
      link: '/digital-transformation',
      cta: t('services.dtCta'),
      pricing: t('services.dtPrice'),
    },
    {
      icon: Code2,
      title: t('services.cdTitle'),
      description: t('services.cdDesc'),
      capabilities: [
        { icon: Code2, text: t('services.cdCap1') },
        { icon: Zap, text: t('services.cdCap2') },
        { icon: Target, text: t('services.cdCap3') },
      ],
      benefits: [
        t('services.cdDeliver1'),
        t('services.cdDeliver2'),
        t('services.cdDeliver3'),
        t('services.cdDeliver4'),
        t('services.cdDeliver5'),
      ],
      link: '/contact',
      cta: t('services.cdCta'),
      pricing: t('services.cdPrice'),
    },
  ];

  return (
    <section className="section bg-gradient-to-br from-gray-50 to-blue-50">
      <div className="container-custom">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
            {t('services.title1')} <span className="gradient-text">{t('services.title2')}</span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            {t('services.subtitle')}
          </p>
        </motion.div>

        {/* Services Grid */}
        <div className="space-y-12">
          {services.map((service, index) => {
            const Icon = service.icon;
            const isEven = index % 2 === 0;

            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: isEven ? -20 : 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className={`grid lg:grid-cols-2 gap-8 items-center ${!isEven ? 'lg:grid-flow-dense' : ''}`}
              >
                {/* Content */}
                <div className={!isEven ? 'lg:col-start-2' : ''}>
                  <div className="flex items-center gap-4 mb-6">
                    <div className="w-16 h-16 rounded-2xl bg-white shadow-lg flex items-center justify-center">
                      <Icon className="w-8 h-8 text-primary-600" />
                    </div>
                    <h3 className="text-3xl font-bold text-gray-900">{service.title}</h3>
                  </div>

                  <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                    {service.description}
                  </p>

                  {/* Capabilities */}
                  <div className="flex flex-wrap gap-4 mb-8">
                    {service.capabilities.map((cap, i) => {
                      const CapIcon = cap.icon;
                      return (
                        <div
                          key={i}
                          className="flex items-center gap-2 bg-white rounded-lg px-4 py-2 shadow-sm"
                        >
                          <CapIcon className="w-4 h-4 text-primary-600" />
                          <span className="text-sm font-medium text-gray-700">{cap.text}</span>
                        </div>
                      );
                    })}
                  </div>

                  {/* Benefits List */}
                  <div className="bg-white rounded-xl p-6 shadow-sm mb-6">
                    <h4 className="font-semibold text-gray-900 mb-4">{t('services.dtDeliverTitle')}</h4>
                    <ul className="space-y-2">
                      {service.benefits.map((benefit, i) => (
                        <li key={i} className="flex items-start gap-2 text-gray-700">
                          <span className="text-primary-600 mt-1">✓</span>
                          <span>{benefit}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* CTA */}
                  <div className="flex flex-wrap items-center gap-4">
                    <Link to={service.link} className="btn btn-primary">
                      {service.cta}
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Link>
                    {service.pricing && <div className="text-sm text-gray-600">{service.pricing}</div>}
                  </div>
                </div>

                {/* Visual */}
                <div className={!isEven ? 'lg:col-start-1 lg:row-start-1' : ''}>
                  <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-200">
                    {/* Illustration placeholder */}
                    <div className="aspect-square bg-gradient-to-br from-primary-100 to-secondary-100 rounded-xl flex items-center justify-center">
                      <Icon className="w-32 h-32 text-primary-600 opacity-20" />
                    </div>
                    
                    {/* Stats */}
                    <div className="grid grid-cols-3 gap-4 mt-6">
                      {[
                        { value: '50+', label: t('services.projects') },
                        { value: '98%', label: t('services.satisfaction') },
                        { value: '24/7', label: t('services.support') },
                      ].map((stat, i) => (
                        <div key={i} className="text-center">
                          <div className="text-2xl font-bold text-primary-600">{stat.value}</div>
                          <div className="text-sm text-gray-600">{stat.label}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* Bottom CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mt-16 bg-white rounded-2xl p-8 shadow-lg"
        >
          <h3 className="text-2xl font-bold text-gray-900 mb-4">
            {t('services.notSure')}
          </h3>
          <p className="text-gray-600 mb-6">
            {t('services.notSureDesc')}
          </p>
          <Link to="/contact" className="btn btn-primary text-lg">
            {t('services.contactUs')}
          </Link>
        </motion.div>
      </div>
    </section>
  );
};

export default Services;
